
import {
    Combobox,
    ComboboxItem,
    ComboboxLabel,
    ComboboxList,
    ComboboxProvider,
} from '@ariakit/react';
import * as RadixPopover from '@radix-ui/react-popover';
import React, { useEffect, useRef, useState } from 'react';

import BaseSelect from '$partials/fields/select.jsx';
import getGoogleMapsKey from '$utilities/google-maps-key';
import IconChevron from '$partials/icons/icon-chevron';
import IconClose from '$partials/icons/icon-close';

const DEBOUNCE_DELAY = 300;

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/fields.d.ts').LocationAutocompleteInput}
 */
export const LocationAutocompleteInput = ({
    bounds,
    onChange,
    onOpenChange,
    origin,
    placeholder,
    value,
}) => {
    const [displayValue, setDisplayValue] = useState(value?.search);
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);

    // const inputRef = useRef();
    // /** @type {React.MutableRefObject<HTMLScriptElement>} */
    const scriptRef = useRef();
    /** @type {React.MutableRefObject<(input: string) => Promise<object[]>>} */
    const getSuggestionsRef = useRef();
    /** @type {React.MutableRefObject<number>} */
    const timeoutRef = useRef();
    /** @type {React.MutableRefObject<HTMLDivElement>} */
    const listboxRef = useRef();
    /** @type {React.MutableRefObject<HTMLInputElement>} */
    const comboboxRef = useRef();

    useEffect(() => {
        const handleGoogle = async () => {
            if (window && comboboxRef.current && origin && bounds) {
                // Setup maps libraries
                const { AutocompleteSuggestion, AutocompleteSessionToken } = await window.google.maps.importLibrary('places');
                const { spherical } = await window.google.maps.importLibrary('geometry');

                const boundsPadding = 5000; // 5km
                const nw = spherical.computeOffset(bounds.nw, boundsPadding, 315);
                const se = spherical.computeOffset(bounds.se, boundsPadding, 135);

                const request = {
                    locationRestriction: bounds ? {
                        north: nw?.lat(),
                        south: se?.lat(),
                        east: se?.lng(),
                        west: nw?.lng(),
                    } : undefined,
                    origin: origin,
                    includedPrimaryTypes: [
                        'locality',
                        'sublocality',
                        'school_district',
                        'route',
                        'postal_code',
                    ],
                    language: 'en-GB',
                    sessionToken: new AutocompleteSessionToken()
                };

                getSuggestionsRef.current = async (input) => {
                    if (timeoutRef.current) {
                        window.clearTimeout(timeoutRef.current);
                    }

                    await new Promise((resolve) => timeoutRef.current = window.setTimeout(resolve, DEBOUNCE_DELAY));

                    const { suggestions: googleSuggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
                        ...request,
                        input: input
                    });

                    let results = [];

                    for (let suggestion of googleSuggestions) {
                        const place = suggestion.placePrediction.toPlace();
                        await place.fetchFields({
                            fields: ['location', 'viewport']
                        });

                        const ne = place.viewport.getNorthEast();
                        const sw = place.viewport.getSouthWest();

                        results.push({
                            name: suggestion.placePrediction.text.toString(),
                            location: place.location.toJSON(),
                            bounds: {
                                nw: {
                                    lat: ne.lat(),
                                    lng: sw.lng()
                                },
                                se: {
                                    lat: sw.lat(),
                                    lng: ne.lng()
                                }
                            }
                        });
                    }

                    return results;
                };
            }
        };

        // Load Google Maps
        if (!window.google && !document.getElementById('gmaps')) {
            window.locationAutocompleteInputGoogleCallback = handleGoogle;
            scriptRef.current = document.createElement('script');
            scriptRef.current.async = true;
            scriptRef.current.id = 'gmaps';
            scriptRef.current.type = 'text/javascript';
            scriptRef.current.src = `https://maps.google.com/maps/api/js?key=${getGoogleMapsKey()}&libraries=places&language=en-GB&region=GB&loading=async&callback=locationAutocompleteInputGoogleCallback`;

            document.body.appendChild(scriptRef.current);
        } else if (!window.google || !window.google.maps) {
            scriptRef.current = document.getElementById('gmaps');
        } else {
            handleGoogle();
        }
    }, []);

    useEffect(() => {
        setDisplayValue(value?.search || '');
    }, [value]);

    useEffect(() => {
        if (typeof onOpenChange === 'function') {
            onOpenChange(open);
        }
    }, [open]);

    /**
     * @param {React.ChangeEvent<HTMLInputElement>} e
     */
    const handleChange = async (e) => {
        setDisplayValue(e.target.value);
        setSuggestions([]);

        if (getSuggestionsRef.current && e.target.value.length >= 2) {
            const _suggestions = await getSuggestionsRef.current(e.target.value);
            setSuggestions(_suggestions);
        }
    };

    const distance = value?.distance ? Number(value.distance) : 500;
    const distanceOptions = [
        { label: 'This area only', value: 0 },
        { label: '500m', value: 500 },
        { label: '1km', value: 1000 },
        { label: '2km', value: 2000 },
        { label: '5km', value: 5000 },
        { label: '10km', value: 10000 },
    ];
    const distanceDisplay = distanceOptions.find((opt) => opt.value === distance)?.label;

    return (
        <RadixPopover.Root
            onOpenChange={setOpen}
            open={open}>
            <ComboboxProvider
                open={open}
                setOpen={setOpen}>
                <ComboboxLabel className='sr-only'>Search</ComboboxLabel>
                <RadixPopover.Anchor asChild>
                    <div className='w-full bg-button-primary-filled-background flex items-center'>
                        <Combobox
                            className='w-full flex-1 button-primary-filled type-style-eyebrow'
                            onChange={handleChange}
                            placeholder={placeholder}
                            ref={comboboxRef}
                            value={displayValue} />

                        {value?.search &&
                            <BaseSelect
                                displayValue={distanceDisplay}
                                name='distance'
                                onChange={(v) => onChange({
                                    ...value,
                                    distance: Number(v)
                                })}
                                options={distanceOptions}
                                triggerClassName='!type-style-caption h-[24px] rounded-[24px] bg-main-background theme-light-alt'
                                value={distance} />
                        }

                        {value?.search &&
                            <button
                                className='button-primary-filled'
                                onClick={() =>{
                                    onChange(null);
                                    setSuggestions([]);
                                }}
                                type='button'>
                                <IconClose />
                            </button>
                        }
                    </div>
                </RadixPopover.Anchor>
                <RadixPopover.Content
                    asChild
                    onInteractOutside={(event) => {
                        const target = event.target;
                        const isCombobox = target === comboboxRef.current;
                        const inListbox = target && listboxRef.current?.contains(target);
                        if (isCombobox || inListbox) {
                            event.preventDefault();
                        }
                    }}
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    sideOffset={8}>
                    <ComboboxList
                        className='location-autocomplete__listbox bg-button-primary-filled-background'
                        ref={listboxRef}
                        role='listbox'>
                        {suggestions.map((suggestion, i) => {
                            return (
                                <ComboboxItem
                                    focusOnHover
                                    className='location-autocomplete__option group button-primary-filled type-style-eyebrow w-full truncate !justify-between cursor-pointer'
                                    key={`${i}-${suggestion.name}`}
                                    onClick={() => {
                                        if (typeof onChange === 'function') {
                                            onChange({
                                                ...suggestion.bounds,
                                                search: suggestion.name,
                                                distance: 500
                                            });
                                        }
                                        setSuggestions([]);
                                    }}
                                    value={suggestion}>
                                    {suggestion.name}
                                    <IconChevron className='hidden group-hover:block group-focus:block rotate-[270deg]' />
                                </ComboboxItem>
                            );
                        })}
                    </ComboboxList>
                </RadixPopover.Content>
            </ComboboxProvider>
        </RadixPopover.Root>
    );
};

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export default LocationAutocompleteInput;
